import type { JSX, ReactNode } from 'react'
import { useEffect, useRef } from 'react'
import { useRouterState } from '@tanstack/react-router'
import { useUiStore } from '@/stores/ui'
import { SideNav } from './SideNav'
import { TopBar } from './TopBar'

/**
 * Каркас приложения. Здесь исправлены ровно те поломки, из-за которых «плыли
 * стили» в прошлой версии:
 *
 *  1. Раньше высоту задавал контент (`min-h-dvh` + flex), поэтому сайдбар
 *     растягивался на всю высоту СТРАНИЦЫ, а индикатор runner'а уезжал в самый
 *     низ, ниже последней карточки. Теперь высота приложения — ровно вьюпорт
 *     (`h-dvh` + `overflow-hidden` у рамки), а сайдбар — колонка этой сетки.
 *  2. Раньше скроллилось окно, а хедер держался на `position: sticky` и при
 *     некоторых раскладках уезжал вверх и наезжал на карточки. Теперь скролл
 *     ровно один и он внутри контентной колонки; хедер — отдельная строка
 *     сетки и физически не может уехать.
 *  3. `min-w-0` на контентной колонке: без него длинный адрес сервера в
 *     карточке растягивал колонку и появлялся горизонтальный скролл всей
 *     страницы.
 *
 * Сетка: `[сайдбар] [хедер / скролл-область]`. На мобиле сайдбар превращается
 * в drawer поверх контента, а колонка остаётся одна.
 */
export function AppFrame({ title, children }: { title: string; children: ReactNode }): JSX.Element {
  const navOpen = useUiStore(state => state.navOpen)
  const setNavOpen = useUiStore(state => state.setNavOpen)
  const pathname = useRouterState({ select: state => state.location.pathname })
  const scrollRef = useRef<HTMLElement>(null)

  // Переход по ссылке закрывает drawer и возвращает скролл в начало: иначе
  // новый экран открывается «посередине» — на месте скролла предыдущего.
  useEffect(() => {
    setNavOpen(false)
    // jsdom не реализует Element.scrollTo — без проверки юнит-тесты падали бы на
    // каждом переходе, хотя в браузере всё работает.
    const node = scrollRef.current
    if (node !== null && typeof node.scrollTo === 'function') node.scrollTo({ top: 0 })
  }, [pathname, setNavOpen])

  // Esc закрывает drawer: у него нет своей кнопки внутри контента.
  useEffect(() => {
    if (!navOpen) return
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setNavOpen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [navOpen, setNavOpen])

  return (
    <div className="grid h-dvh grid-cols-1 overflow-hidden lg:grid-cols-[auto_1fr]">
      <a
        href="#main"
        className="sr-only focus-visible:not-sr-only focus-visible:absolute focus-visible:top-3 focus-visible:left-3 focus-visible:z-toast focus-visible:rounded-sm focus-visible:bg-honey focus-visible:px-3 focus-visible:py-2 focus-visible:text-honey-ink"
      >
        Перейти к содержимому
      </a>

      {/* Постоянный сайдбар от 1024px. Своя высота = высота рамки. */}
      <SideNav className="hidden lg:flex" />

      {/* Drawer на мобиле. Подложка достаточно плотная, чтобы контент под ней
          не спорил с меню (scrim 60%). */}
      {navOpen ? (
        <div className="fixed inset-0 z-overlay flex lg:hidden">
          <button
            type="button"
            aria-label="Закрыть меню"
            onClick={() => setNavOpen(false)}
            className="anim-backdrop absolute inset-0 cursor-default bg-abyss/70 backdrop-blur-sm"
          />
          <SideNav className="anim-pop relative flex" onClose={() => setNavOpen(false)} />
        </div>
      ) : null}

      <div className="grid min-w-0 grid-rows-[auto_1fr] overflow-hidden">
        <TopBar title={title} />

        {/* Единственная скролл-область приложения. `overscroll-contain` не даёт
            прокрутке «протечь» на страницу, когда список кончился. */}
        <main
          ref={scrollRef}
          id="main"
          className="min-w-0 overflow-y-auto overscroll-contain px-4 pt-5 pb-16 sm:px-6"
        >
          <div className="mx-auto flex w-full max-w-app flex-col gap-6">{children}</div>
        </main>
      </div>
    </div>
  )
}
